import { useEffect, useCallback, useRef } from 'react'

export interface ShortcutConfig {
  /** Single key (e.g. 'k') OR a two-key sequence written as 'g d' (press g then d). */
  key: string
  ctrlKey?: boolean
  altKey?: boolean
  shiftKey?: boolean
  action: () => void
  description: string
  category: 'Navigation' | 'Actions' | 'General'
}

const IGNORED_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  if (IGNORED_TAGS.has(target.tagName)) return true
  if (target.isContentEditable) return true
  return false
}

function isSequence(key: string): boolean {
  return key.includes(' ')
}

function matchesShortcut(e: KeyboardEvent, shortcut: ShortcutConfig): boolean {
  const keyMatch = (e.key ?? '').toLowerCase() === (shortcut.key ?? '').toLowerCase()
  const ctrlMatch = !!shortcut.ctrlKey === (e.ctrlKey || e.metaKey)
  const altMatch = !!shortcut.altKey === e.altKey
  const shiftMatch = !!shortcut.shiftKey === e.shiftKey
  return keyMatch && ctrlMatch && altMatch && shiftMatch
}

/**
 * Global keyboard-shortcut hook (cabinet portal). Supports modifier combos,
 * single keys, and Gmail-style leader sequences (`g d` = press g then d within 1s).
 * Never fires while typing in an input/textarea/contenteditable.
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]): void {
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  const leaderRef = useRef<string | null>(null)
  const leaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearLeader = useCallback(() => {
    leaderRef.current = null
    if (leaderTimerRef.current) {
      clearTimeout(leaderTimerRef.current)
      leaderTimerRef.current = null
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return

      const list = shortcutsRef.current
      const key = (e.key ?? '').toLowerCase()

      if (leaderRef.current && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const combo = `${leaderRef.current} ${key}`
        const seq = list.find(
          (s) => isSequence(s.key) && (s.key ?? '').toLowerCase() === combo,
        )
        clearLeader()
        if (seq) {
          e.preventDefault()
          seq.action()
          return
        }
      }

      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const startsSequence = list.some(
          (s) => isSequence(s.key) && (s.key ?? '').toLowerCase().startsWith(`${key} `),
        )
        if (startsSequence) {
          e.preventDefault()
          leaderRef.current = key
          if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current)
          leaderTimerRef.current = setTimeout(clearLeader, 1000)
          return
        }
      }

      for (const shortcut of list) {
        if (isSequence(shortcut.key)) continue
        if (matchesShortcut(e, shortcut)) {
          e.preventDefault()
          e.stopPropagation()
          shortcut.action()
          return
        }
      }
    },
    [clearLeader],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current)
    }
  }, [handleKeyDown])
}

/**
 * Default global shortcuts for the cabinet (adminclient) portal. Routes map to
 * the cabinet's PRO modules + core destinations.
 */
export function getDefaultShortcuts(
  navigate: (path: string) => void,
  setCommandPaletteOpen: (open: boolean) => void,
): ShortcutConfig[] {
  return [
    {
      key: 'k',
      ctrlKey: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Palette de commandes (rechercher / naviguer / créer)',
      category: 'General',
    },
    {
      key: '/',
      action: () => {
        const el =
          document.querySelector<HTMLInputElement>('[data-search-input]') ||
          document.querySelector<HTMLInputElement>('header input[type="text"], header input:not([type])')
        if (el) el.focus()
        else setCommandPaletteOpen(true)
      },
      description: 'Rechercher',
      category: 'General',
    },
    {
      key: 'n',
      action: () => setCommandPaletteOpen(true),
      description: 'Nouveau / Créer…',
      category: 'Actions',
    },
    { key: 'g d', action: () => navigate('/dashboard'), description: 'Aller au tableau de bord', category: 'Navigation' },
    { key: 'g m', action: () => navigate('/mandats'), description: 'Aller aux mandats', category: 'Navigation' },
    { key: 'g r', action: () => navigate('/risk-pro'), description: 'Aller à RiskPRO', category: 'Navigation' },
    { key: 'g g', action: () => navigate('/governance-pro'), description: 'Aller à GovernancePRO', category: 'Navigation' },
    { key: 'g c', action: () => navigate('/compliance-pro'), description: 'Aller à CompliancePRO', category: 'Navigation' },
    { key: 'g a', action: () => navigate('/audit-pro'), description: 'Aller à AuditPRO', category: 'Navigation' },
    { key: 'g s', action: () => navigate('/security-pro'), description: 'Aller à SecurityPRO', category: 'Navigation' },
    { key: 'g t', action: () => navigate('/data-pro'), description: 'Aller à DataPRO', category: 'Navigation' },
    { key: 'g l', action: () => navigate('/clients'), description: 'Aller aux clients', category: 'Navigation' },
    { key: 'g p', action: () => navigate('/settings'), description: 'Ouvrir les paramètres', category: 'Navigation' },
  ]
}
